import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone, Mail, MapPin, Clock, Send, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { companyInfo, services } from "../data/data";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}api`;


// Page Header
const PageHeader = () => (
  <section className="bg-pc-primary pt-32 pb-20 texture-grain">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-[2px] bg-pc-accent" />
        <span className="text-pc-accent font-heading text-sm font-semibold uppercase tracking-[0.15em]">
          Get In Touch
        </span>
      </div>
      <h1 className="font-heading text-3xl md:text-4xl lg:text-[52px] font-extrabold text-white leading-[1.1] tracking-tight mb-5">
        Contact Us
      </h1>
      <p className="font-body text-white/50 text-lg max-w-2xl leading-relaxed">
        Have a project in mind? Need an estimate? We're here to help.
        Reach out and we'll get back to you promptly.
      </p>
    </motion.div>
  </section>
);

// Contact Info
const ContactInfoItem = ({ icon: Icon, title, value, href }) => (
  <div className="flex items-start gap-4">
    <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-pc-accent/8 flex items-center justify-center">
      <Icon className="w-5 h-5 text-pc-accent" />
    </div>
    <div>
      <p className="text-[10px] font-heading font-semibold text-pc-muted uppercase tracking-[0.15em] mb-1">{title}</p>
      {href ? (
        <a href={href} className="font-body text-pc-primary text-sm font-medium hover:text-pc-accent transition-colors duration-200">
          {value}
        </a>
      ) : (
        <p className="font-body text-pc-primary text-sm font-medium">{value}</p>
      )}
    </div>
  </div>
);

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service: "", message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      await axios.post(`${API}/inquiries`, formData);
      setIsSubmitted(true);
      toast.success("Your inquiry has been submitted.");
    } catch (err) {
      console.error("Failed to submit inquiry:", err);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({ name: "", email: "", phone: "", service: "", message: "" });
  };

  return (
    <main>
      <PageHeader />

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <div className="accent-bar mb-5" />
              <h2 className="font-heading text-2xl font-extrabold text-pc-primary mb-2">
                Send Us an Inquiry
              </h2>
              <p className="font-body text-pc-muted text-sm mb-8">
                Fill in the details below and we'll get in touch within 24 hours.
              </p>

              {isSubmitted ? (
                <Card className="border-pc-border/60 rounded-xl">
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-pc-primary mb-2">
                      Inquiry Submitted
                    </h3>
                    <p className="font-body text-pc-muted text-sm mb-6 max-w-md mx-auto">
                      Thank you for reaching out. We've received your inquiry and
                      will respond within 24 hours.
                    </p>
                    <Button
                      onClick={resetForm}
                      variant="outline"
                      className="border-pc-accent text-pc-accent hover:bg-pc-accent hover:text-white rounded-lg font-heading font-semibold transition-colors duration-200"
                    >
                      Submit Another Inquiry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-heading text-sm text-pc-text font-semibold">
                        Full Name <span className="text-pc-accent">*</span>
                      </Label>
                      <Input
                        id="name" name="name" placeholder="Your full name"
                        value={formData.name} onChange={handleChange}
                        className="h-11 rounded-lg border-pc-border/80 font-body focus:border-pc-accent focus:ring-pc-accent/20"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-heading text-sm text-pc-text font-semibold">
                        Email Address
                      </Label>
                      <Input
                        id="email" name="email" type="email" placeholder="your@email.com"
                        value={formData.email} onChange={handleChange}
                        className="h-11 rounded-lg border-pc-border/80 font-body focus:border-pc-accent focus:ring-pc-accent/20"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-heading text-sm text-pc-text font-semibold">
                        Phone Number <span className="text-pc-accent">*</span>
                      </Label>
                      <Input
                        id="phone" name="phone" placeholder="+91 XXXXX XXXXX"
                        value={formData.phone} onChange={handleChange}
                        className="h-11 rounded-lg border-pc-border/80 font-body focus:border-pc-accent focus:ring-pc-accent/20"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="font-heading text-sm text-pc-text font-semibold">
                        Service Required
                      </Label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger className="h-11 rounded-lg border-pc-border/80 font-body">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>
                              {s.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-heading text-sm text-pc-text font-semibold">
                      Project Details <span className="text-pc-accent">*</span>
                    </Label>
                    <Textarea
                      id="message" name="message"
                      placeholder="Tell us about your project — location, type of work, approximate size, timeline, or any specific requirements."
                      value={formData.message} onChange={handleChange}
                      className="min-h-[140px] rounded-lg border-pc-border/80 font-body focus:border-pc-accent focus:ring-pc-accent/20 resize-y"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="bg-pc-accent hover:bg-pc-accent/90 text-white text-sm font-heading font-semibold px-8 h-12 rounded-lg transition-colors duration-200"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit Inquiry
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Sidebar */}
            <div>
              <h2 className="font-heading text-2xl font-extrabold text-pc-primary mb-2">
                Contact Details
              </h2>
              <p className="font-body text-pc-muted text-sm mb-8">
                Reach us directly.
              </p>

              <div className="space-y-6">
                <ContactInfoItem icon={Phone} title="Phone" value={companyInfo.phone} href={`tel:${companyInfo.phone}`} />
                <ContactInfoItem icon={Mail} title="Email" value={companyInfo.email} href={`mailto:${companyInfo.email}`} />
                <ContactInfoItem icon={MapPin} title="Office" value={companyInfo.address} />
                <ContactInfoItem icon={Clock} title="Hours" value="Mon – Sat: 9:00 AM – 7:00 PM" />
              </div>

              <div className="mt-10 p-6 rounded-xl bg-pc-light border border-pc-border/40">
                <h3 className="font-heading text-sm font-bold text-pc-primary mb-4">
                  What Happens Next?
                </h3>
                <ol className="space-y-4 font-body text-sm text-pc-muted">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pc-accent text-white text-xs flex items-center justify-center font-heading font-bold">
                      1
                    </span>
                    We review your inquiry within 24 hours.
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pc-accent text-white text-xs flex items-center justify-center font-heading font-bold">
                      2
                    </span>
                    We discuss requirements over a call or site visit.
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pc-accent text-white text-xs flex items-center justify-center font-heading font-bold">
                      3
                    </span>
                    We share a detailed, transparent estimate.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
